import { useCallback, useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import { ModalButtonColor, ModalType, WalletType } from '~/shared';
import { groupItems } from '../components/ListTab';
import { useLayoutSelector } from '../redux';

const useBuy = (activeVariants: Record<string, number>) => {
	const activeList = useLayoutSelector((state) => state.clothesShop.activeList);
	const activeListIndex = useLayoutSelector(
		(state) => state.clothesShop.activeListIndex,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.clothesShop.activeCategory,
	);
	const shopItems = useLayoutSelector((state) => state.clothesShop.shopItems);
	const activeItemId = useLayoutSelector(
		(state) => state.clothesShop.activeItemId,
	);
	const searchValue = useLayoutSelector(
		(state) => state.clothesShop.searchValue,
	);
	const tabIndex = useLayoutSelector((state) => state.clothesShop.tabIndex);
	const ownedItems = useLayoutSelector((state) => state.clothesShop.ownedItems);
	const modalState = useObservable(rage.modal.getCurrentModal());

	const items = useMemo(() => {
		const hasSearchValue = searchValue.trim().length > 0;

		if (tabIndex === 0) {
			if (hasSearchValue) {
				return groupItems(
					shopItems
						.filter((item) =>
							item.name.toLowerCase().includes(searchValue.toLowerCase()),
						)
						.map((item) => ({
							...item,
							active: false,
							new: false,
						})),
				);
			}
			return groupItems(
				shopItems
					.filter((item) => item.category === activeCategory)
					.map((item) => ({
						...item,
						active: false,
						new: false,
					})),
			);
		}

		return groupItems(
			ownedItems
				.filter((item) => item.category === activeCategory)
				.map((item) => ({
					...item,
					active: false,
					new: false,
				})),
		);
	}, [tabIndex, shopItems, ownedItems, activeCategory, searchValue]);

	const item = useMemo(() => {
		const foundByActiveId = items.find((group) =>
			group.items.some((item) => item.id === activeItemId),
		);
		if (foundByActiveId) {
			return foundByActiveId;
		}

		return activeList[activeListIndex || 0];
	}, [items, activeItemId, activeList, activeListIndex]);

	const variationItem = useMemo(() => {
		if (!item?.id) return;

		const variantIndex = activeVariants[item.id] || 0;
		return items.find((i) => i.id === item.id)?.items[variantIndex];
	}, [items, activeVariants, item]);

	const handleBuy = useCallback(
		async (walletType?: WalletType) => {
			if (!variationItem?.id || modalState) {
				return;
			}

			const text = `Вы действительно хотите оплатить ${
				walletType === WalletType.Cash
					? 'наличными'
					: walletType === WalletType.Bank
					? 'картой'
					: 'донат-валютой'
			} товар "${variationItem.name}"`;

			rage.modal.showModal(
				{
					type: ModalType.Price,
					title: 'Подтверждение',
					text: text,
					price: variationItem.price,
					wallet:
						variationItem.wallet === WalletType.Donate
							? WalletType.Donate
							: walletType || WalletType.Cash,
					acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
					cancelButton: {
						text: 'Отменить',
						color: ModalButtonColor.Secondary,
					},
				},
				(result) => {
					if (result.isAccepted) {
						if (variationItem.wallet === WalletType.Donate) {
							rage.clothesShop.buy(variationItem.id, WalletType.Donate);
							return;
						}

						rage.clothesShop.buy(
							variationItem.id,
							walletType || WalletType.Cash,
						);
					}
				},
			);
		},
		[variationItem, modalState],
	);

	return { handleBuy };
};

export { useBuy };
